import {Controller, UseFormReturn} from 'react-hook-form';
import {useTranslation} from 'react-i18next';
import './styles.scss';
import FlatInput from '../../../../../../../components/flat-input/flat-input';
import {ProfessionalCaregiverInviteFormType} from '../../../../../../../enums/professional-caregiver-invite-form-type';
import {CollaboratorFormInputs} from './renderer';
import {translations} from '../../../../../../../i18n/translations';
import Switch from '../../../../../../../components/switch/switch';
import {FlatDateInput} from '../../../../../../../components/flat-date-input/flat-date-input';
import {CLEAN_STRINGS} from '../../../../../../../utils/constants/regex';
import {
  CUSTOM_NAME_MAX_CHARACTERS,
  FULL_NAME_MAX_CHARACTERS,
} from '../../../../../../../utils/constants';

interface FormProps {
  formType: ProfessionalCaregiverInviteFormType;
  formInstance: UseFormReturn<CollaboratorFormInputs>;
}

const Form = ({
  formInstance: {
    control,
    register,
    getValues,
    watch,
    formState: {errors},
  },
}: FormProps) => {
  const {t} = useTranslation();

  watch(['name', 'displayName']);

  return (
    <form id="add-senior-form">
      <FlatInput
        label={t(translations.seniors.root.modals.add_senior_flow.form.inputs.name.label)}
        placeholder={t(
          translations.seniors.root.modals.add_senior_flow.form.inputs.name.placeholder
        )}
        {...register('name', {
          required: {
            value: true,
            message: t(
              translations.seniors.root.modals.add_senior_flow.form.inputs.name.required_message
            ),
          },
          maxLength: {
            value: FULL_NAME_MAX_CHARACTERS,
            message: t(
              translations.seniors.root.modals.add_senior_flow.form.inputs.name.max_message,
              {
                characters: FULL_NAME_MAX_CHARACTERS,
              }
            ),
          },
          pattern: {
            value: CLEAN_STRINGS,
            message: t(
              translations.seniors.root.modals.add_senior_flow.form.inputs.name.pattern_message
            ),
          },
        })}
        errors={errors.name?.message}
        counter={{
          current: getValues('name')?.length ?? 0,
          max: FULL_NAME_MAX_CHARACTERS,
        }}
        required
      />

      <Controller
        name="isActive"
        control={control}
        render={({field: {onChange, value}}) => (
          <Switch
            label={t(translations.seniors.root.modals.add_senior_flow.form.inputs.active)}
            {...{onChange, value, checked: value}}
          />
        )}
      />

      <FlatInput
        label={t(translations.seniors.root.modals.add_senior_flow.form.inputs.name_alias.label)}
        placeholder={t(
          translations.seniors.root.modals.add_senior_flow.form.inputs.name_alias.placeholder
        )}
        {...register('displayName', {
          required: {
            value: true,
            message: t(
              translations.seniors.root.modals.add_senior_flow.form.inputs.name_alias
                .required_message
            ),
          },
          pattern: {
            value: CLEAN_STRINGS,
            message: t(
              translations.seniors.root.modals.add_senior_flow.form.inputs.name_alias
                .pattern_message
            ),
          },
          maxLength: {
            value: CUSTOM_NAME_MAX_CHARACTERS,
            message: t(
              translations.seniors.root.modals.add_senior_flow.form.inputs.name_alias.max_message,
              {
                characters: CUSTOM_NAME_MAX_CHARACTERS,
              }
            ),
          },
        })}
        errors={errors.displayName?.message}
        counter={{
          current: getValues('displayName')?.length ?? 0,
          max: CUSTOM_NAME_MAX_CHARACTERS,
        }}
        required
      />

      <Controller
        name="birthday"
        control={control}
        rules={{
          required: {
            value: true,
            message: t(
              translations.seniors.root.modals.add_senior_flow.form.inputs.birthdate
                .required_message
            ),
          },
        }}
        render={({field: {onChange, value}, fieldState: {error}}) => (
          <FlatDateInput
            label={t(translations.user_form.birthday_input)}
            placeholder={t(translations.user_form.date_placeholder_input)}
            disableFuture={true}
            errors={error?.message}
            {...{onChange, value}}
          />
        )}
      />
    </form>
  );
};

export default Form;
